import { SiteHeaderActions } from "./site-header-actions";

import { buildHref } from "@softmax/web-addresses";
import {
  FeatureLink,
  useFeatureNavigation,
} from "@softmax/web-features/navigation";
import { NavigationReady } from "@softmax/web-features/navigation/markers";
import { type SiteLink } from "@softmax/web-features/site/records";
import { PillSearch } from "./search/pill-search";
import { SiteHeaderAccountMenu } from "./pill-account-menu";
import { SiteHeaderNavigation } from "./navigation/pill-site-navigation";
import { usePillMenu } from "./use-pill-menu";
import { useSiteHeader } from "./use-site-header";
import { isNavigationClick } from "./link-click";
import styles from "./account-pill.module.css";

import type { PillViewer } from "./pill-viewer";

export function AccountPill({
  viewer,
  hydrated,
  leagues,
  onOpenChange,
  onNavigationAccepted,
}: {
  viewer: PillViewer | null;
  hydrated: boolean;
  leagues: SiteLink[];
  onOpenChange: (open: boolean) => void;
  onNavigationAccepted: (href: string) => void;
}) {
  const { pathname } = useFeatureNavigation();
  const { ids, bar, input, panel, setPanel, onNavigate, disclosure } =
    useSiteHeader({
      pathname,
      hydrated,
      onOpenChange,
      onNavigationAccepted,
    });
  const data = usePillMenu({
    member: viewer !== null,
    playersNeeded: true,
  });
  const home = buildHref({ name: "home" });

  return (
    <header
      ref={bar}
      className={styles.bar}
      data-account-pill=""
      data-site-header=""
      data-coworld={data.coworld ? "" : undefined}
      data-open={panel ?? undefined}
    >
      {hydrated && <NavigationReady stage="shell" />}
      <FeatureLink
        href={home}
        className={styles.brand}
        aria-label="Softmax home"
        onClick={(event) => {
          if (isNavigationClick(event)) onNavigate(home);
        }}
      >
        <img
          className={styles.mark}
          src="/home/nav-brandmark-color.svg"
          alt=""
        />
        <img
          className={styles.wordmark}
          src="/home/nav-wordmark-black.png"
          alt=""
        />
      </FeatureLink>
      <SiteHeaderNavigation
        data={data}
        member={viewer !== null}
        leagues={leagues}
        ids={ids}
        panel={panel}
        hydrated={hydrated}
        setPanel={setPanel}
        onNavigate={onNavigate}
        disclosure={disclosure}
      />
      <SiteHeaderActions
        viewer={viewer}
        agent={data.agent}
        disclosure={disclosure}
        onNavigate={onNavigate}
      />
      {panel === "account" && viewer && (
        <div id={`${ids}-account`} className={styles.panel}>
          <span className={styles.heading}>{viewer.name}</span>
          <SiteHeaderAccountMenu data={data} member onNavigate={onNavigate} />
        </div>
      )}
      {panel === "search" && (
        <dialog
          open
          id={`${ids}-search`}
          aria-label="Search Softmax"
          className={`${styles.panel} ${styles.searchPanel}`}
        >
          <PillSearch
            input={input}
            label="Search Softmax"
            placeholder="Search Softmax…"
            scope={viewer ? "both" : "site"}
            member={viewer !== null}
            onNavigate={onNavigate}
            all={
              viewer
                ? (q) =>
                    buildHref({
                      name: "search",
                      search: new URLSearchParams({ q, from: pathname }),
                    })
                : null
            }
          >
            {null}
          </PillSearch>
        </dialog>
      )}
    </header>
  );
}
